import { every } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { createSelector } from 'reselect';

import PurchaseAgreement, {
  IProps as IPurchaseAgreementProps,
} from '../../components/brokerageOfferFlow/PurchaseAgreement';
import { brokerageOfferPath } from '../../routes/pathBuilders';
import {
  maybeAcceptInvitation,
  onSubmitPurchaseAgreement,
  updatePurchaseAgreement,
} from '../../store/brokerageOfferFlow/actions';
import * as brokerageOfferFlowSelectors from '../../store/brokerageOfferFlow/selectors';
import OfferLayout, { IProps as IOfferLayoutProps } from './OfferLayout';

type IPurchaseAgreementPageProps = IOfferLayoutProps &
  IPurchaseAgreementProps & {
    hasValidPurchaseAgreement: boolean;
    onSubmitPurchaseAgreement: () => void;
  };

const PurchaseAgreementPage: React.SFC<IPurchaseAgreementPageProps> = props => {
  const {
    brokerageInfo,
    hasAcceptedInvitation,
    hasValidPurchaseAgreement,
    onSubmitPurchaseAgreement,
    match,
  } = props;

  const prevUrl = brokerageOfferPath(match.params.token, 'details');

  return (
    <OfferLayout
      brokerageInfo={brokerageInfo}
      disclaimerType="trusted-buyer"
      footerType={hasAcceptedInvitation ? 'BROKERAGE' : 'NONE'}
      headerType="BROKERAGE"
      onPrimaryBtnClick={onSubmitPurchaseAgreement}
      prevBtnText="Back"
      prevBtnUrl={prevUrl}
      primaryBtnDisabled={!hasValidPurchaseAgreement}
      primaryBtnText="Next"
    >
      <PurchaseAgreement {...props} />
    </OfferLayout>
  );
};

const mapStateToProps = createSelector(
  brokerageOfferFlowSelectors.brokerageInfo,
  brokerageOfferFlowSelectors.invitationData,
  brokerageOfferFlowSelectors.purchaseAgreement,
  brokerageOfferFlowSelectors.streetAddress,
  (
    brokerageInfo,
    invitationData,
    purchaseAgreement,
    streetAddress,
  ): Partial<IPurchaseAgreementProps> => {
    const shouldAcceptInvitation = !!invitationData;
    const hasAcceptedInvitation =
      shouldAcceptInvitation && !!invitationData.get('invitation_accepted');

    const { closeDate, hasHoa, hoaFees, hoaName, signerNames } = purchaseAgreement;

    const hasCloseDate = !!closeDate;
    const hasHoaName = !!(hoaName && hoaName.trim());
    const hasHoaFees =
      hoaFees && every(hoaFees, hoaFee => hoaFee['hoaFeeCents'] && hoaFee['hoaFeeFrequency']);
    const hasSignerNames = signerNames && every(signerNames);

    const hasValidPurchaseAgreement =
      hasAcceptedInvitation &&
      hasCloseDate &&
      hasHoa != null && // hoa must be answered
      (!hasHoa || (hasHoaName && hasHoaFees)) && // no hoa or has name and valid fees
      hasSignerNames; // non null signerNames

    return {
      brokerageInfo,
      hasAcceptedInvitation,
      hasValidPurchaseAgreement,
      shouldAcceptInvitation,
      streetAddress,
      ...purchaseAgreement,
    };
  },
);

const mapDispatchToProps = {
  maybeAcceptInvitation,
  onReturnClick: returnUrl => push(returnUrl),
  onSubmitPurchaseAgreement,
  onUpdate: (key: string, value) => updatePurchaseAgreement({ [key]: value }),
};

// tslint:disable-next-line
// https://stackoverflow.com/questions/42657792/typescript-react-redux-property-xxx-does-not-exist-on-type-intrinsicattrib
export default connect<
  Partial<IPurchaseAgreementProps>,
  IPurchaseAgreementProps,
  Partial<IPurchaseAgreementProps>
>(
  mapStateToProps,
  mapDispatchToProps,
)(PurchaseAgreementPage);
